import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Carousel from '@wsb/guac-widget-shared/lib/components/Carousel';
import CarouselArrow from './CarouselArrow';
import { ICONS, WIDTHS } from './Constants';
import VIEW_DEVICES from '../../../constants/viewDevices';
import StaticWrapper from './StaticWrapper';
import wrapWithDeviceDetection from '../../wrapWithDeviceDetection';
import { constants } from '@wsb/guac-widget-core';

const { renderModes } = constants;

const styles = {
  slide: {
    opacity: 1
  },
  track: {
    display: 'flex'
  }
};

const trackStyles = ({ slideWidth }) => ({
  marginLeft: `${(slideWidth + WIDTHS.CELL_PADDING) / 2}px`,
  ...styles.track
});

const getMobileStyleProps = widgetPaddingPx => {
  return {
    marginLeft: `-${widgetPaddingPx.left}`,
    marginRight: `-${widgetPaddingPx.right}`
  };
};

const getSlideWidth = ({
  isMobile,
  isInsetWithOverflow,
  containerWidth,
  slidesToShow,
  viewDevice
}) => {
  if (isMobile) {
    const defaultSlideWidth = containerWidth - WIDTHS.MOBILE_SLIDE_OFFSET;
    return viewDevice === VIEW_DEVICES.MOBILE
      ? Math.min(defaultSlideWidth, WIDTHS.PREVIEW_MOBILE_MAX)
      : Math.min(defaultSlideWidth, WIDTHS.SLIDE_MAX);
  }
  if (isInsetWithOverflow) {
    return Math.floor(
      (containerWidth - WIDTHS.ARROW_OFFSET * 2 - (slidesToShow - 1) * WIDTHS.CELL_PADDING) /
        slidesToShow
    );
  }
  return Math.floor((containerWidth - (slidesToShow - 1) * WIDTHS.CELL_PADDING) / slidesToShow);
};

/* eslint-disable complexity */
const CarouselWrapper = ({ children, currentAssetIndex, viewDevice, size, renderMode }) => {
  const [containerWidthPx, setContainerWidthPx] = useState(0);
  const [widgetMarginPx, setWidgetMarginPx] = useState({ left: '', right: '' });
  const [widgetPaddingPx, setWidgetPaddingPx] = useState({ left: '', right: '' });

  useEffect(() => {
    const container = document.querySelector('[data-container-id="fp-carousel-wrapper"]');
    if (container) {
      const containerStyles = window.getComputedStyle(container);
      setContainerWidthPx(containerStyles.width);
    }
    const widget = document.querySelector('[data-container-id="ols-featured-products"]');
    if (widget) {
      const widgetStyles = window.getComputedStyle(widget);
      const newWidgetMargin = { left: widgetStyles.marginLeft, right: widgetStyles.marginRight };
      const newWidgetPadding = { left: widgetStyles.paddingLeft, right: widgetStyles.paddingRight };
      setWidgetMarginPx(newWidgetMargin);
      setWidgetPaddingPx(newWidgetPadding);
    }
  }, [size]);

  const isMobile =
    (size === 'xs' && renderMode === renderModes.PUBLISH) || viewDevice === VIEW_DEVICES.MOBILE;
  const isInset =
    !isMobile &&
    ((size === 'sm' && renderMode === renderModes.PUBLISH) ||
      parseInt(widgetMarginPx.left, 10) === 0 ||
      parseInt(widgetMarginPx.right, 10) === 0);
  const containerWidth = parseInt(containerWidthPx, 10);
  const slidesToShow = isMobile
    ? 1
    : Math.floor(containerWidth / (WIDTHS.CELL_PADDING + WIDTHS.SLIDE_MIN));
  const initialSlide = Math.floor(slidesToShow / 2);
  const hasMoreThanVisible = children.length > slidesToShow;
  const isInsetWithOverflow = isInset && hasMoreThanVisible;
  const slideWidth = getSlideWidth({
    isMobile,
    isInsetWithOverflow,
    containerWidth,
    slidesToShow,
    viewDevice
  });

  const arrowControls = [
    {
      component: CarouselArrow,
      position: 'top',
      props: { icon: ICONS.LEFT, currentAssetIndex }
    },
    {
      component: CarouselArrow,
      position: 'bottom',
      props: { icon: ICONS.RIGHT, currentAssetIndex }
    }
  ];

  return (
    <UX2.Element.Block
      data-container-id='fp-carousel-wrapper'
      style={ isMobile && getMobileStyleProps(widgetPaddingPx) }
    >
      <UX2.Element.Block
        style={
          isInsetWithOverflow && {
            marginLeft: `${WIDTHS.ARROW_OFFSET}px`,
            marginRight: `${WIDTHS.ARROW_OFFSET}px`
          }
        }
      >
        { slidesToShow > 0 &&
          (hasMoreThanVisible ? (
            <Carousel
              style={{
                ...styles,
                track: slidesToShow % 2 ? styles.track : trackStyles({ slideWidth })
              }}
              lazyLoad={ false }
              viewportWidth='100%'
              initialSlide={ initialSlide }
              children={ children }
              dots={ false }
              arrows={ false }
              draggable={ isMobile }
              cellPadding={ WIDTHS.CELL_PADDING }
              slideWidth={ slideWidth }
              controls={ !isMobile ? arrowControls : [] }
              infinite
            />
          ) : (
            <StaticWrapper children={ children } slideWidth={ slideWidth } />
          )) }
      </UX2.Element.Block>
    </UX2.Element.Block>
  );
};

CarouselWrapper.propTypes = {
  children: PropTypes.array,
  currentAssetIndex: PropTypes.number,
  viewDevice: PropTypes.string,
  renderMode: PropTypes.string,
  size: PropTypes.string
};

export default wrapWithDeviceDetection(CarouselWrapper);
